import { DeviceOutcome } from './types';

/**
 * Extracts a comparable error name from whatever `getUserMedia` (or, defensively,
 * an Agora track-creation call) threw.
 *
 * - Standard browser failures are `DOMException`s with a `.name`
 *   (NotAllowedError, NotFoundError, NotReadableError, OverconstrainedError, ...).
 * - Agora wraps failures in an error carrying a `.code` (e.g. 'PERMISSION_DENIED',
 *   'NOT_READABLE'). We normalise those onto the DOMException names so a single
 *   mapping table covers both.
 */
const getErrorName = (error: unknown): string => {
  if (!error || typeof error !== 'object') return 'UnknownError';

  // Agora RTC errors expose a string `code`; map the ones we care about.
  const code = (error as { code?: unknown }).code;
  if (typeof code === 'string') {
    switch (code) {
      case 'PERMISSION_DENIED':
        return 'NotAllowedError';
      case 'DEVICE_NOT_FOUND':
        return 'NotFoundError';
      case 'NOT_READABLE':
      case 'MEDIA_OPTION_INVALID':
        return 'NotReadableError';
      case 'CONSTRAINT_NOT_SATISFIED':
        return 'OverconstrainedError';
      case 'NOT_SUPPORTED':
        return 'NotSupportedError';
      default:
        break;
    }
  }

  const name = (error as { name?: unknown }).name;
  return typeof name === 'string' ? name : 'UnknownError';
};

/**
 * Pure mapping from an acquisition failure to a {@link DeviceOutcome}.
 *
 * `browserPermissionAfter` is the result of querying `navigator.permissions` for
 * this device kind *after* the failure. It is what lets us split the three cases
 * that share the same `NotAllowedError`:
 *   - permission reports `granted`  → the OS is blocking      → SystemDenied
 *   - permission reports `prompt`   → the prompt was dismissed → Dismissed
 *   - permission reports `denied`/unknown → a real block        → PermissionDenied
 *
 * Pass `null` for `browserPermissionAfter` when the Permissions API is
 * unavailable (e.g. older Safari); we then fall back to the safe `PermissionDenied`.
 *
 * Never returns `Granted` — this is only called on the failure path.
 */
export const mapAcquisitionError = (error: unknown, browserPermissionAfter: PermissionState | null): DeviceOutcome => {
  switch (getErrorName(error)) {
    // NotAllowedError (+ legacy aliases some browsers still emit).
    // We trust the post-failure permission STATE over the error name: even if a
    // browser reports `PermissionDismissedError`, a `denied` state means the user
    // is actually blocked, so we return PermissionDenied (not Dismissed) and route
    // them to the reload-and-grant path rather than a futile re-prompt.
    case 'NotAllowedError':
    case 'PermissionDeniedError':
    case 'PermissionDismissedError':
      if (browserPermissionAfter === 'granted') return DeviceOutcome.SystemDenied;
      if (browserPermissionAfter === 'prompt') return DeviceOutcome.Dismissed;
      return DeviceOutcome.PermissionDenied;

    case 'NotFoundError':
    case 'DevicesNotFoundError':
      return DeviceOutcome.NoDevice;

    // OverconstrainedError: a specific device (e.g. a remembered deviceId) could
    // not satisfy the constraints — usually because it was unplugged.
    case 'OverconstrainedError':
    case 'ConstraintNotSatisfiedError':
      return DeviceOutcome.NoDevice;

    // NotReadable: device exists but couldn't be opened. AbortError: read was
    // aborted (e.g. device yanked mid-acquire).
    case 'NotReadableError':
    case 'TrackStartError':
    case 'AbortError':
      return DeviceOutcome.DeviceUnavailable;

    case 'SecurityError':
    case 'NotSupportedError':
    case 'TypeError':
      return DeviceOutcome.Unsupported;

    // Unknown failure. Treat as device-unavailable (a real failure the caller must
    // react to) rather than swallowing it back into a "keep waiting" state — that
    // swallow is exactly the stuck-overlay bug on main.
    default:
      return DeviceOutcome.DeviceUnavailable;
  }
};
